import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logoImg from "../../assets/images/logo1.png";

const sections = [
  { id: "definitions", title: "Definitions" },
  { id: "introduction", title: "Welcome and Introduction" },
  { id: "disclaimers", title: "Back Relief Plan Disclaimers" },
  { id: "acceptance", title: "Acceptance of Terms" },
  { id: "account", title: "Account Eligibility and Security" },
  { id: "subscription", title: "Subscription Services" },
  { id: "refund", title: "Refund Policy" },
  { id: "intellectual-property", title: "Intellectual Property Rights" },
  { id: "user-content", title: "User Content and Rights" },
  { id: "service-level", title: "Service Level Agreement (SLA)" },
  { id: "technical", title: "Technical Requirements" },
  { id: "support", title: "Customer Support and Complaints" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "disputes", title: "Dispute Resolution" },
  { id: "modifications", title: "Service Modifications" },
  { id: "termination", title: "Account Termination" },
  { id: "legal", title: "Legal Information" },
];

const List = ({ items }) => (
  <ul className="mb-2 list-disc space-y-1 pl-6">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const Section = ({ id, number, title, children }) => (
  <section id={id} className="mb-8 scroll-mt-6">
    <h3 className="mb-2 text-xl font-bold">
      {number}. {title.toUpperCase()}
    </h3>
    {children}
  </section>
);

const Subheading = ({ children }) => (
  <h4 className="mb-1 mt-4 font-semibold">{children}</h4>
);

const Label = ({ children }) => (
  <span className="mb-1 block font-semibold">{children}</span>
);

function TermsConditions() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <header className="w-full border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center px-4 py-6">
          <Link to="/" aria-label="MoveRelief home">
            <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
          </Link>
        </div>
      </header>

      <div className="w-full bg-[#7C3A8D]">
        <div className="mx-auto max-w-5xl px-4 py-8">
          <h1 className="text-3xl font-bold text-white md:text-4xl">Terms &amp; Conditions</h1>
        </div>
      </div>

      <main className="w-full">
        <div className="mx-auto max-w-3xl px-4 py-10 text-left text-base leading-relaxed text-gray-800">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-6 text-sm font-medium text-gray-600 hover:text-[#7C3A8D]"
          >
            ← Back
          </button>

          <p className="mb-6 rounded-lg border border-[#7C3A8D]/20 bg-[#F8F6F0] p-4 text-sm">
            Need account access details while reviewing these terms? Contact us at{" "}
            <a className="text-[#7C3A8D] underline" href="mailto:info@moverelief.pro">
              info@moverelief.pro
            </a>{" "}
            for assistance.
          </p>

          <h2 className="mb-4 text-2xl font-bold">TABLE OF CONTENTS</h2>
          <ol className="mb-8 list-decimal space-y-1 pl-6">
            {sections.map((section, index) => (
              <li key={section.id}>
                <a className="text-[#7C3A8D] underline hover:text-[#5F2D6F]" href={`#${section.id}`}>
                  {index + 1}. {section.title}
                </a>
              </li>
            ))}
          </ol>

          <Section id="definitions" number="1" title="Definitions">
            <Subheading>1.1 Service and Platform Terms</Subheading>
            <List
              items={[
                'MoveRelief: Operating as MoveRelief.pro ("we," "us," or "our") through Momiji Group Inc.',
                "Service: All features, functionalities, programs, checkout flows, posture scan flows, plans, and content available through MoveRelief.",
                "Platform: Our website, applications, and related services accessible via any device.",
                'User: Any individual or entity accessing or using our services ("you" or "your").',
              ]}
            />

            <Subheading>1.2 Back Relief Plan Terms</Subheading>
            <List
              items={[
                "Back Relief Plan: A digital plan generated from your answers and optional posture scan inputs to provide general movement and comfort guidance.",
                "Posture Scan: An optional camera- or image-based feature that may help assess posture and movement patterns.",
                "Movement Guidance: Gentle exercise, posture, mobility, and habit suggestions provided for informational purposes.",
                "Plan Results: Recommendations, explanations, routines, tips, and other outputs generated by the Service.",
              ]}
            />

            <Subheading>1.3 Subscription Terms</Subheading>
            <List
              items={[
                "Subscription: Recurring paid access to premium features.",
                "Trial Period: Initial 7-day access period available for $1.00.",
                "Standard Subscription: Recurring paid access following the trial period.",
                "Billing Cycle: The frequency of subscription charges, including quarterly billing.",
              ]}
            />

            <Subheading>1.4 Security and Technical Terms</Subheading>
            <List
              items={[
                "Authentication Code: Time-sensitive, single-use verification code for account access.",
                "Account Security: Methods and procedures for protecting user access.",
                "Access Credentials: Information used to verify user identity.",
                "Force Majeure: Circumstances beyond reasonable control, including natural disasters, government actions, war, cyber attacks, infrastructure failures, pandemics, internet outages, power failures, or other similar events.",
              ]}
            />
          </Section>

          <Section id="introduction" number="2" title="Welcome and Introduction">
            <Subheading>2.1 Platform Overview</Subheading>
            <p className="mb-2">
              Welcome to MoveRelief. Our digital platform provides personalized back relief plans, posture and movement tips, and gentle exercise guidance based on your answers and optional scan inputs.
            </p>

            <Subheading>2.2 Agreement to Terms</Subheading>
            <List
              items={[
                "You agree to these Terms & Conditions.",
                "You accept our Privacy Policy.",
                "You consent to our data practices as described in our Privacy Policy.",
                "You acknowledge the service limitations and health-related disclaimers below.",
              ]}
            />

            <Subheading>2.3 Access Methods</Subheading>
            <List items={["Web browsers.", "Mobile devices.", "Tablet computers.", "Desktop devices.", "Any future access methods we support."]} />
          </Section>

          <Section id="disclaimers" number="3" title="Back Relief Plan Disclaimers">
            <Subheading>3.1 Informational and Educational Purpose</Subheading>
            <List
              items={[
                "MoveRelief provides back relief plans, posture scan features, and movement guidance for informational and educational purposes only.",
                "Our services are not medical advice and are not a substitute for professional diagnosis, treatment, physical therapy, or clinical evaluation.",
                "Plan results are intended to support general comfort, mobility awareness, and daily movement habits.",
                "Results should not be used for emergency, diagnostic, therapeutic, or medical decision-making purposes.",
              ]}
            />

            <Subheading>3.2 Service Limitations</Subheading>
            <List
              items={[
                "Results are based on the information, images, and inputs you provide.",
                "Results may be influenced by lighting, camera quality, clothing, posture during scanning, device settings, and accuracy of submitted information.",
                "Results may vary from user to user and are subject to interpretation.",
                "We do not guarantee pain reduction, improved mobility, injury prevention, or any specific health outcome.",
              ]}
            />

            <Subheading>3.3 No Professional Medical Advice</Subheading>
            <List
              items={[
                "Our services do not replace consultation with qualified health professionals.",
                "We do not provide medical, physiotherapy, chiropractic, diagnostic, or emergency advice.",
                "If you have severe pain, injury, numbness, weakness, fever, loss of bladder or bowel control, or any urgent symptoms, seek medical care immediately.",
                "Before starting any exercise or movement routine, consult a licensed professional if you have a medical condition, injury, pregnancy, recent surgery, or uncertainty about safety.",
              ]}
            />

            <Subheading>3.4 User Responsibility</Subheading>
            <List
              items={[
                "All use is at your own risk and discretion.",
                "Stop any movement that causes pain, dizziness, numbness, shortness of breath, or discomfort beyond mild effort.",
                "No guarantees of specific results, satisfaction, improvement, or pain relief are made.",
                "We are not liable for decisions, exercises, purchases, health choices, or actions taken based on plan results.",
              ]}
            />
          </Section>

          <Section id="acceptance" number="4" title="Acceptance of Terms">
            <Subheading>4.1 Agreement to Terms</Subheading>
            <List
              items={[
                "By accessing or using MoveRelief.pro and our services, you acknowledge that you have read, understood, and agree to be bound by these terms.",
                "If you do not agree with any part of these terms, you must not use our services.",
                "Your continued use of our services following any changes constitutes acceptance of those changes.",
              ]}
            />

            <Subheading>4.2 Modifications to Terms</Subheading>
            <List
              items={[
                "We reserve the right to modify these terms at any time.",
                "We may notify users of material changes by email or through the platform.",
                "Changes become effective immediately for new users and, where required, after reasonable notice for existing users.",
                "Your continued use after changes indicates acceptance of updated terms.",
              ]}
            />
          </Section>

          <Section id="account" number="5" title="Account Eligibility and Security">
            <Subheading>5.1 Age Requirements</Subheading>
            <List items={["Minimum age: 18 years.", "You must have legal capacity to enter into these terms.", "Age verification may be required."]} />

            <Subheading>5.2 Account Creation</Subheading>
            <List items={["One account per user.", "Valid email required.", "Accurate information required.", "You must keep account information up to date."]} />

            <Subheading>5.3 Security Requirements</Subheading>
            <List items={["Secure email management.", "Protection of access codes.", "Prompt reporting of unauthorized access.", "Maintenance of accurate information."]} />

            <Subheading>5.4 Prohibited Activities</Subheading>
            <List items={["Multiple accounts to abuse trials or promotions.", "Credential sharing.", "False information.", "Unauthorized access.", "Security bypasses.", "Fraudulent payment activity."]} />

            <Subheading>5.5 Data Retention Policy</Subheading>
            <p className="mb-2">
              To support security and efficiency, we may remove inactive accounts and associated data after extended inactivity, subject to legal, billing, dispute, and fraud-prevention requirements.
            </p>
          </Section>

          <Section id="subscription" number="6" title="Subscription Services">
            <Subheading>6.1 Subscription Options</Subheading>
            <List
              items={[
                "7-day trial period.",
                "One-time trial charge of $1.00.",
                "Access to premium plan features during the trial.",
                "The trial converts to the selected recurring subscription after the trial unless canceled before renewal.",
              ]}
            />

            <Subheading>Standard Subscription Pricing</Subheading>
            <List
              items={[
                "After the 7-day trial, the standard subscription is $29.99 every 3 months unless another price is clearly shown during signup.",
                "Regional pricing, taxes, VAT, currency conversion, promotions, and local payment methods may affect the exact amount shown at checkout.",
                "You will be charged the exact amount shown during signup.",
                "Any future subscription rate changes will be communicated in advance where required.",
              ]}
            />

            <Subheading>Trial Payment Processing</Subheading>
            <List
              items={[
                "Your $1.00 trial payment may be processed as one charge or, in some regions or payment methods, as multiple smaller charges totaling $1.00.",
                "The trial payment activates trial access and may help confirm that the payment method supports recurring billing.",
                "The total trial amount remains $1.00 regardless of how processing is split.",
              ]}
            />

            <Subheading>6.2 Payment and Billing</Subheading>
            <List
              items={[
                "PCI-DSS compliant payment processing through third-party providers.",
                "Major credit/debit cards and select regional payment methods may be supported.",
                "Automatic billing occurs on subscription renewal.",
                "Failed payments may be retried, and access may be suspended after repeated failed payments.",
                "Bank fees, currency conversion fees, and declined payment fees charged by your financial institution are your responsibility.",
              ]}
            />

            <Subheading>6.3 Subscription Management</Subheading>
            <List
              items={[
                "You may cancel online in your personal account where available or by contacting support.",
                "Customer support email: info@moverelief.pro.",
                "All subscriptions auto-renew until canceled.",
                "Cancel before renewal to avoid future charges.",
              ]}
            />

            <Subheading>Post-Cancellation Terms</Subheading>
            <List
              items={[
                "Service continues until the current paid period ends unless otherwise required by law.",
                "No automatic renewals occur after cancellation is processed.",
                "No partial refunds are provided for unused time unless required by law or expressly stated in these terms.",
                "Account reactivation may be available at current rates.",
              ]}
            />
          </Section>

          <Section id="refund" number="7" title="Refund Policy">
            <Subheading>7.1 Trial Period Refunds ($1.00)</Subheading>
            <List
              items={[
                "Eligible for full refund within 30 days of purchase if you are a first-time trial user.",
                "No previous trial refunds on any account.",
                "No terms of service violations.",
                "Request submitted through official channels.",
              ]}
            />

            <Subheading>7.2 Regular Subscription Refunds</Subheading>
            <p className="mb-2">Regular subscription payments are not eligible for refunds except in specific cases:</p>
            <List
              items={[
                "Service Unavailability: Documented downtime exceeding 24 consecutive hours not caused by scheduled maintenance or Force Majeure events.",
                "Technical Issues: Platform bugs preventing core feature access, reported within 30 days of the charge and verified by our technical team.",
                "Billing Errors: Duplicate charges, incorrect amount charged, or unauthorized transactions.",
                "Legal Requirements: As required by applicable laws in your jurisdiction.",
              ]}
            />

            <Subheading>7.3 Non-Refundable Cases</Subheading>
            <List
              items={[
                "Partial or unused subscription time after cancellation.",
                "Plan downgrades.",
                "Feature unavailability during scheduled maintenance.",
                "Change of mind or no longer needing the service.",
                "Failure to cancel before renewal.",
                "Dissatisfaction with results where the service was available and delivered as described.",
              ]}
            />

            <Subheading>7.4 Refund Process</Subheading>
            <List
              items={[
                "Submit a request by email: info@moverelief.pro.",
                "Include your account email, transaction details, reason for refund, and supporting documentation.",
                "Typical processing time: 3 business days after all required information is received.",
              ]}
            />

            <Subheading>7.5 Currency Processing</Subheading>
            <List
              items={[
                "Payments may be processed in USD, EUR, GBP, CAD, AUD, or another supported currency depending on your region.",
                "Exchange rates are determined by your bank, card issuer, or payment processor.",
                "Currency conversion fees charged by your bank are not refundable and are not controlled by MoveRelief.",
              ]}
            />
          </Section>

          <Section id="intellectual-property" number="8" title="Intellectual Property Rights">
            <Subheading>8.1 Ownership Rights</Subheading>
            <List
              items={[
                "All platform content, design, functionality, text, images, workflows, plan templates, and software are owned by MoveRelief or its licensors.",
                "Content is protected by international copyright, trademark, and other intellectual property laws.",
                "All trademarks and service marks are proprietary.",
              ]}
            />

            <Subheading>8.2 Licensed Content</Subheading>
            <List items={["Personal, non-commercial use only.", "Non-transferable license.", "No modification permitted.", "No source code extraction.", "No redistribution allowed."]} />

            <Subheading>8.3 Prohibited Actions</Subheading>
            <List items={["Reverse engineering.", "Content reproduction.", "Copyright notice removal.", "Derivative works creation.", "Unauthorized distribution."]} />
          </Section>

          <Section id="user-content" number="9" title="User Content and Rights">
            <Subheading>9.1 User Ownership</Subheading>
            <List
              items={[
                "Users retain ownership of submitted content, including profile information, questionnaire responses, images, scan inputs, and custom content.",
                "Personal data remains subject to our Privacy Policy.",
              ]}
            />

            <Subheading>9.2 License to MoveRelief</Subheading>
            <p className="mb-2">
              You grant MoveRelief a non-exclusive, worldwide, royalty-free license to use submitted content as necessary to operate, provide, secure, troubleshoot, and improve the Service.
            </p>

            <Subheading>9.3 Content Usage Rights</Subheading>
            <List
              items={[
                "Display or process user content to provide the Service.",
                "Optimize content for technical purposes.",
                "Analyze usage in aggregated or anonymized form for improvements.",
                "Use anonymized statistics and performance metrics.",
              ]}
            />

            <Subheading>9.4 Content Restrictions</Subheading>
            <List items={["Do not submit copyrighted material without permission.", "Do not submit malicious code.", "Do not submit harmful, deceptive, unlawful, or inappropriate material.", "Do not impersonate others or provide fraudulent information."]} />
          </Section>

          <Section id="service-level" number="10" title="Service Level Agreement (SLA)">
            <Subheading>10.1 Service Availability</Subheading>
            <List items={["We aim to provide reliable service availability.", "Maintenance, third-party outages, and Force Majeure events are excluded.", "Performance monitoring may be used to improve reliability."]} />

            <Subheading>10.2 Change Categories</Subheading>
            <Label>A. Standard Changes</Label>
            <List items={["Feature updates.", "Interface improvements.", "Performance optimization.", "User communication where material."]} />
            <Label>B. Emergency Changes</Label>
            <List items={["Critical security updates.", "Legal compliance requirements.", "System stability issues.", "Immediate implementation when required."]} />

            <Subheading>10.3 Maintenance Windows</Subheading>
            <List items={["Scheduled maintenance may be announced in advance when practical.", "Emergency maintenance may occur immediately.", "Maintenance is generally planned during lower-usage periods when possible."]} />
          </Section>

          <Section id="technical" number="11" title="Technical Requirements">
            <Subheading>11.1 Browser Support</Subheading>
            <List items={["Google Chrome version 90 or later.", "Mozilla Firefox version 88 or later.", "Safari version 14 or later.", "Microsoft Edge Chromium version 90 or later."]} />

            <Subheading>11.2 Display and Device Requirements</Subheading>
            <List items={["Minimum screen resolution: 320x480 pixels.", "Responsive design support.", "Camera access may be required for optional posture scan features.", "Modern mobile or desktop device recommended."]} />

            <Subheading>11.3 Connection and System Requirements</Subheading>
            <List items={["Stable internet connection.", "JavaScript enabled.", "Cookies and local storage enabled.", "Secure connection capability (HTTPS).", "Latest security patches recommended."]} />
          </Section>

          <Section id="support" number="12" title="Customer Support and Complaints">
            <Subheading>12.1 Support Availability</Subheading>
            <List items={["Customer support is available through email.", "Average response time may vary based on request volume.", "All inquiries may be tracked with internal reference numbers."]} />

            <Subheading>12.2 Support Channels</Subheading>
            <List items={["Primary support email: info@moverelief.pro.", "Self-service options may include account management, subscription changes, troubleshooting guidance, FAQs, and automated assistance."]} />

            <Subheading>12.3 Complaint Resolution Process</Subheading>
            <List
              items={[
                "Submit your full name and account email.",
                "Provide a detailed issue description, timeline, supporting documentation, and previous communication references.",
                "Submit complaints to: info@moverelief.pro.",
                "We will review the account history, technical details, and available documentation before providing a response.",
              ]}
            />
          </Section>

          <Section id="liability" number="13" title="Limitation of Liability">
            <Subheading>Scope of Limitations</Subheading>
            <p className="mb-2">
              While we strive to provide reliable and high-quality services, MoveRelief liability is limited to the maximum extent permitted by law. Our maximum aggregate liability for claims arising from or related to our services shall not exceed the amount paid by you for the services during the twelve (12) months preceding the claim.
            </p>

            <Subheading>Excluded Damages</Subheading>
            <List
              items={[
                "Lost profits, revenue, business opportunities, or data.",
                "Indirect, consequential, incidental, punitive, or special damages.",
                "Temporary service interruptions, data transmission errors, third-party failures, device compatibility issues, or internet connectivity problems.",
                "Health, comfort, mobility, lifestyle, emotional, personal, or professional outcomes based on use of the Service.",
              ]}
            />

            <Subheading>Service-Related Limitations</Subheading>
            <List
              items={[
                "Accuracy of posture scan or movement pattern outputs.",
                "Effectiveness of any movement guidance, exercise, routine, or back relief plan.",
                "Achievement of specific pain reduction, mobility, comfort, wellness, or health outcomes.",
                "Decisions made based on our assessments, recommendations, or content.",
              ]}
            />

            <Subheading>Indemnification Obligations</Subheading>
            <p className="mb-2">
              You agree to indemnify, defend, and hold harmless MoveRelief, Momiji Group Inc., and their officers, directors, employees, and agents from claims arising from your violation of these terms, misuse of the Service, submitted content, unlawful activity, or infringement of third-party rights.
            </p>
          </Section>

          <Section id="disputes" number="14" title="Dispute Resolution">
            <Subheading>Governing Law and Jurisdiction</Subheading>
            <p className="mb-2">
              These terms are governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to conflict-of-law principles.
            </p>

            <Subheading>14.1 Informal Dispute Resolution</Subheading>
            <p className="mb-2">
              Before initiating formal proceedings, we encourage you to contact us directly at info@moverelief.pro. We will make good-faith efforts to resolve issues through direct communication.
            </p>

            <Subheading>14.2 Arbitration and Individual Claims</Subheading>
            <List
              items={[
                "Where permitted by law, disputes will be resolved individually and not as part of a class or representative action.",
                "You waive the right to participate in class actions, class-wide arbitration, consolidated claims, or representative actions to the extent permitted by law.",
                "Small claims court, intellectual property disputes, and urgent equitable relief may be handled outside arbitration where applicable.",
              ]}
            />
          </Section>

          <Section id="modifications" number="15" title="Service Modifications">
            <Subheading>15.1 Modification Rights</Subheading>
            <p className="mb-2">
              MoveRelief reserves the right to modify, suspend, or discontinue any aspect of our services, with or without notice, subject to applicable law and the notice practices described here.
            </p>

            <Subheading>15.2 Types of Changes</Subheading>
            <List
              items={[
                "Addition or removal of features.",
                "User interface improvements.",
                "Performance optimizations.",
                "Security enhancements.",
                "Technical requirement updates.",
                "Content, routine, guidance, or assessment updates.",
              ]}
            />

            <Subheading>15.3 User Rights</Subheading>
            <List
              items={[
                "Users may be notified of significant changes affecting service use.",
                "Users may terminate their subscription if they do not agree with material changes.",
                "Continued use after changes constitutes acceptance.",
                "Changes will not affect prior completed transactions except as required by law.",
              ]}
            />
          </Section>

          <Section id="termination" number="16" title="Account Termination">
            <Subheading>16.1 User-Initiated Termination</Subheading>
            <List
              items={[
                "You may request account termination by contacting info@moverelief.pro.",
                "Termination may be requested at any time.",
                "No termination fees apply.",
                "Termination is subject to current billing cycle completion and refund terms.",
              ]}
            />

            <Subheading>16.2 MoveRelief-Initiated Termination</Subheading>
            <p className="mb-2">We may suspend or terminate accounts for:</p>
            <List
              items={[
                "Terms violations, fraud, abusive behavior, security violations, payment defaults, account sharing, or false information.",
                "Legal or regulatory requirements, court orders, investigations, government requests, jurisdictional restrictions, or age verification failures.",
              ]}
            />

            <Subheading>16.3 Post-Termination Procedures</Subheading>
            <List
              items={[
                "Service access may be discontinued.",
                "Authentication codes may be deactivated.",
                "Account data may be retained as required by law, billing, dispute, fraud-prevention, and privacy obligations.",
                "Subscription cancellation and any applicable refund review will be handled under these terms.",
              ]}
            />
          </Section>

          <Section id="legal" number="17" title="Legal Information">
            <Subheading>17.1 Corporate Identity</Subheading>
            <List items={["Company: Momiji Group Inc.", "Address: 2604 Whittier Place, Wilmington, DE, 19808, United States.", "Country: United States."]} />

            <Subheading>17.2 Official Communications</Subheading>
            <List items={["Email: info@moverelief.pro.", "Website: https://moverelief.pro.", "Correspondence: 2604 Whittier Place, Wilmington, DE, 19808, United States."]} />
          </Section>
        </div>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-5xl px-4 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-md">
              <Link to="/" className="mb-4 inline-block" aria-label="MoveRelief home">
                <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
              </Link>
              <p className="text-sm leading-relaxed text-gray-400">
                MoveRelief.pro provides personalized back relief plans, posture scan flows, and gentle movement guidance designed for everyday comfort.
              </p>
            </div>

            <div>
              <h3 className="mb-3 font-semibold">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/terms">Terms of Service</Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/cookie">Cookie Policy</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-800 pt-6 text-sm text-gray-400">
            © 2026 Momiji Group Inc. All Rights Reserved.
            <br />
            2604 Whittier Place, Wilmington, DE, 19808
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsConditions;
